import { readonly } from 'vue';
import { Logger } from '~~/helpers/logger';
import { buildPartnersLevels } from '~~/helpers/loyaltyPoints/buildPartnersLevels';
import type { CustomerDataPlatformLevels, PartnersLevel } from '~~/types/partnersLevels/partnersLevels';

/**
 * Loyalty levels from the Customer Data Platform: each level's point and approximate spend thresholds
 * and the points-to-currency ratio, joined with the discount from useCTLoyaltyLevels by level number.
 * Only for the Partners pages, so the extra request is not made shop-wide.
 *
 * The discount is not taken from the Customer Data Platform: commercetools both stores and applies the
 * loyalty discounts, so it stays their source of truth.
 */
export const useCDPLoyaltyLevels = () => {

  const customerDataPlatformLevels = useState<CustomerDataPlatformLevels | null>('partnersLevelsData', () => null);
  const loading = useState<boolean>('partnersLevelsLoading', () => false);
  const error = useState<boolean>('partnersLevelsError', () => false);
  const { loyaltyLevels, load: loadLoyaltyLevels } = useCTLoyaltyLevels();

  const loadCustomerDataPlatformLevels = async () => {

    if (loading.value || customerDataPlatformLevels.value) {

      return;

    }

    loading.value = true;
    error.value = false;

    try {

      customerDataPlatformLevels.value = await $fetch<CustomerDataPlatformLevels>(
        '/customer-data-platform/loyalty-levels',
      );

    } catch (e) {

      error.value = true;
      Logger.error(`partners-levels|fetch: Failed to fetch loyalty levels - ${e}`);

    } finally {

      loading.value = false;

    }

  };

  const load = () => Promise.all([loadLoyaltyLevels(), loadCustomerDataPlatformLevels()]);

  const partnersLevels = computed<PartnersLevel[]>(() => buildPartnersLevels(
    loyaltyLevels.value ?? [],
    customerDataPlatformLevels.value?.levels ?? [],
  ));

  const ratio = computed<number | null>(() => customerDataPlatformLevels.value?.ratio ?? null);

  const currency = computed<string | null>(() => customerDataPlatformLevels.value?.currency ?? null);

  return {
    partnersLevels,
    ratio,
    currency,
    loading: readonly(loading),
    error: readonly(error),
    load,
  };

};
